import { FilePresent, FindInPage } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DocumentOperationOrigin, editDocuments } from 'lib/api/document';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import SmallHeaderWithIcon from 'lib/designSystem/SmallHeaderWithIcon';
import {
  formatDateBack,
  formatMoney,
  formatMoneyBack,
  showAlert,
  validaRut,
} from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { type Business, type DocumentWithMetadata } from '../../types';

export interface EditDocumentManuallyProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  document: DocumentWithMetadata | null;
  business: Business | null;
  onDocumentUpdated?: (document: DocumentWithMetadata) => void;
  origin?: DocumentOperationOrigin;
}

interface DocumentOriginConfig {
  identifierKey: 'stakeholderIdentifier' | 'businessIdentifier';
  identifierLabel: string;
  nameKey: 'stakeholderName' | 'businessName';
  getDocumentIdentifier: (doc: DocumentWithMetadata) => string;
  getDocumentName: (doc: DocumentWithMetadata) => string;
  getEditedIdentifier: (state: EditedDocumentState) => string;
  setEditedIdentifier: (
    state: EditedDocumentState,
    value: string,
  ) => EditedDocumentState;
}

const DOCUMENT_ORIGIN_CONFIG: Record<
  DocumentOperationOrigin,
  DocumentOriginConfig
> = {
  [DocumentOperationOrigin.FACTORING]: {
    identifierKey: 'stakeholderIdentifier',
    identifierLabel: 'RUT deudor',
    nameKey: 'stakeholderName',
    getDocumentIdentifier: (doc) => doc.stakeholderIdentifier || '',
    getDocumentName: (doc) => doc.stakeholderName || '',
    getEditedIdentifier: (state) => state.stakeholderIdentifier,
    setEditedIdentifier: (state, value) => ({
      ...state,
      stakeholderIdentifier: value,
    }),
  },
  [DocumentOperationOrigin.CONFIRMING]: {
    identifierKey: 'businessIdentifier',
    identifierLabel: 'RUT proveedor',
    nameKey: 'businessName',
    getDocumentIdentifier: (doc) => doc.businessIdentifier || '',
    getDocumentName: (doc) => doc.businessName || '',
    getEditedIdentifier: (state) => state.businessIdentifier || '',
    setEditedIdentifier: (state, value) => ({
      ...state,
      businessIdentifier: value,
    }),
  },
};

interface EditedDocumentState {
  folio: string;
  stakeholderIdentifier: string;
  businessIdentifier?: string;
  stakeholderName: string;
  businessName?: string;
  issuedDate: string;
  amount: number;
  subType: string;
}

const CODE_TO_SUBTYPE_MAPPING: Record<string, string> = {
  '33': 'electronic-invoice',
  '34': 'electronic-invoice-exempt',
  '46': 'electronic-invoice-buyer',
};

const convertCodeToSubType = (code: string | undefined | null): string => {
  if (!code) return '';
  return CODE_TO_SUBTYPE_MAPPING[code] || code;
};

const isInvoice = (document: DocumentWithMetadata | null): boolean => {
  if (!document) return false;
  const code =
    (document as { subType?: string; code?: string }).subType ||
    (document as { subType?: string; code?: string }).code ||
    '';
  return code in CODE_TO_SUBTYPE_MAPPING;
};

const convertDateToDDMMYYYY = (
  dateString: string | undefined | null,
): string => {
  if (!dateString) return '';

  if (dateString.length === 8 && /^\d+$/.test(dateString)) {
    return dateString;
  }

  try {
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      const day = date.getUTCDate().toString().padStart(2, '0');
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      const year = date.getUTCFullYear().toString();
      return `${day}${month}${year}`;
    }
  } catch (error) {
    console.error('Error converting date:', error);
  }

  return '';
};

const EditDocumentManually = (props: EditDocumentManuallyProps) => {
  const {
    open,
    setOpen,
    document,
    business,
    onDocumentUpdated,
    origin = DocumentOperationOrigin.FACTORING,
  } = props;
  void business;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const originConfig = DOCUMENT_ORIGIN_CONFIG[origin];

  const [editedDocument, setEditedDocument] = useState<EditedDocumentState>({
    folio: '',
    stakeholderIdentifier: '',
    stakeholderName: '',
    issuedDate: '',
    amount: 0,
    subType: '',
  });

  useEffect(() => {
    if (document) {
      const docSubType =
        (document as { subType?: string; code?: string }).subType ||
        (document as { subType?: string; code?: string }).code ||
        '';

      const baseState: EditedDocumentState = {
        folio: document.folio || '',
        stakeholderIdentifier: document.stakeholderIdentifier || '',
        stakeholderName: document.stakeholderName || '',
        issuedDate: convertDateToDDMMYYYY(document.issuedDate),
        amount: document.amount || 0,
        subType: convertCodeToSubType(docSubType),
      };

      if (origin === DocumentOperationOrigin.CONFIRMING) {
        baseState.businessIdentifier =
          originConfig.getDocumentIdentifier(document);
        baseState.businessName = originConfig.getDocumentName(document);
      }

      setEditedDocument(baseState);
    }
  }, [document, origin, originConfig]);

  const validateRuts = () => {
    const identifier = originConfig.getEditedIdentifier(editedDocument);
    if (validaRut(identifier)) {
      return true;
    }
    return false;
  };

  const checkAllFields = () => {
    const identifier = originConfig.getEditedIdentifier(editedDocument);
    if (
      editedDocument.folio === '' ||
      identifier === '' ||
      editedDocument.issuedDate === '' ||
      editedDocument.amount === 0
    ) {
      showAlert(dispatch, 'error', 'Debes llenar todos los campos');
      return false;
    }
    return true;
  };

  const handleChangeDate = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const [year, month, day] = e.target.value.split('-');
    setEditedDocument({
      ...editedDocument,
      issuedDate: `${day}${month}${year}`,
    });
  };

  const convertDateToISO = (dateString: string): string | undefined => {
    if (!dateString || dateString.length !== 8) return undefined;
    const day = dateString.slice(0, 2);
    const month = dateString.slice(2, 4);
    const year = dateString.slice(4, 8);
    const date = new Date(`${year}-${month}-${day}`);
    if (isNaN(date.getTime())) return undefined;
    return date.toISOString();
  };

  const handleSave = async () => {
    if (!checkAllFields()) return;
    if (!validateRuts()) {
      showAlert(dispatch, 'error', 'RUT inválido');
      return;
    }

    if (!document?.id) {
      showAlert(dispatch, 'error', 'No se pudo identificar el documento');
      return;
    }

    setLoading(true);
    try {
      const documentToEdit: {
        id: string;
        folio?: string;
        stakeholderIdentifier?: string;
        businessIdentifier?: string;
        subType?: string;
        issuedDate?: string;
        amount?: number;
        dueDate?: string;
      } = {
        id: document.id,
      };

      const currentIdentifier = originConfig.getDocumentIdentifier(document);
      const editedIdentifier = originConfig.getEditedIdentifier(editedDocument);

      if (editedIdentifier && editedIdentifier !== currentIdentifier) {
        if (origin === DocumentOperationOrigin.FACTORING) {
          documentToEdit.stakeholderIdentifier = editedIdentifier;
        } else if (origin === DocumentOperationOrigin.CONFIRMING) {
          documentToEdit.businessIdentifier = editedIdentifier;
        }
      }

      if (editedDocument.folio && editedDocument.folio !== document.folio) {
        documentToEdit.folio = String(editedDocument.folio);
      }

      if (isInvoice(document)) {
        const docSubType =
          (document as { subType?: string; code?: string }).subType ||
          (document as { subType?: string; code?: string }).code ||
          '';
        const currentSubType = convertCodeToSubType(docSubType);

        if (
          editedDocument.subType &&
          editedDocument.subType !== currentSubType
        ) {
          documentToEdit.subType = editedDocument.subType || undefined;
        }
      }

      if (
        editedDocument.issuedDate &&
        editedDocument.issuedDate !== document.issuedDate
      ) {
        const isoDate = convertDateToISO(editedDocument.issuedDate);
        if (isoDate) {
          documentToEdit.issuedDate = isoDate;
        }
      }

      if (
        editedDocument.amount !== undefined &&
        editedDocument.amount !== document.amount
      ) {
        documentToEdit.amount = editedDocument.amount;
      }

      if (document.dueDate) {
        try {
          const date = new Date(document.dueDate);
          if (!isNaN(date.getTime())) {
            documentToEdit.dueDate = date.toISOString();
          }
        } catch (error) {
          console.error('Error converting dueDate:', error);
        }
      }

      const hasSubTypeChange =
        isInvoice(document) && documentToEdit.subType !== undefined;

      const hasIdentifierChange =
        (origin === DocumentOperationOrigin.FACTORING &&
          documentToEdit.stakeholderIdentifier !== undefined) ||
        (origin === DocumentOperationOrigin.CONFIRMING &&
          documentToEdit.businessIdentifier !== undefined);

      if (
        !hasIdentifierChange &&
        !hasSubTypeChange &&
        !documentToEdit.issuedDate &&
        documentToEdit.amount === undefined
      ) {
        showAlert(dispatch, 'warning', 'No hay cambios para guardar');
        setLoading(false);
        return;
      }

      const response = await editDocuments(dispatch, {
        documents: [documentToEdit],
        origin,
      });

      if (!response) {
        showAlert(dispatch, 'error', 'Error al actualizar el documento');
        setLoading(false);
        return;
      }

      if (response.errors && response.errors.length > 0) {
        const errorMessage =
          response.errors[0]?.error || 'Error al actualizar el documento';
        showAlert(dispatch, 'error', errorMessage);
        setLoading(false);
        return;
      }

      if (response.updatedDocuments && response.updatedDocuments.length > 0) {
        const updatedDoc = response.updatedDocuments[0];

        if (document && onDocumentUpdated) {
          const updatedDocument: DocumentWithMetadata = {
            ...document,
            stakeholderIdentifier: updatedDoc.stakeholderIdentifier,
            businessIdentifier: updatedDoc.businessIdentifier,
            issuedDate: updatedDoc.issuedDate,
            amount: updatedDoc.amount,
            amountAvailable: updatedDoc.amount,
            folio: editedDocument.folio,
            stakeholderName:
              origin === DocumentOperationOrigin.FACTORING
                ? editedDocument.stakeholderName
                : document.stakeholderName,
            businessName:
              origin === DocumentOperationOrigin.CONFIRMING
                ? editedDocument.businessName
                : document.businessName,
            code: updatedDoc.subType || undefined,
            dueDate: updatedDoc.dueDate || document.dueDate,
          };
          (updatedDocument as { subType?: string }).subType =
            updatedDoc.subType || undefined;
          onDocumentUpdated(updatedDocument);
        }
      }

      setOpen(false);
      showAlert(dispatch, 'success', 'Documento actualizado correctamente');
    } catch (error: unknown) {
      console.error(error);
      const errorMessage =
        (
          error as {
            response?: { data?: { message?: string } };
            message?: string;
          }
        )?.response?.data?.message ||
        (error as { message?: string })?.message ||
        'Error al actualizar el documento';
      showAlert(dispatch, 'error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!document) {
    return null;
  }

  return (
    <Dialog maxWidth="md" open={open} onClose={() => setOpen(false)}>
      <DialogTitle>
        <HeaderWithActions title="Editar documento" type="dialogHeader" />
      </DialogTitle>
      {loading ? (
        <CenteredCircularProgress type="dialog" />
      ) : (
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 2 }}>
            <Box>
              <SmallHeaderWithIcon
                icon={<FilePresent fontSize="small" />}
                title="Información del documento"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Folio"
                    value={editedDocument.folio}
                    onChange={(e) =>
                      setEditedDocument({
                        ...editedDocument,
                        folio: e.target.value,
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label={`${originConfig.identifierLabel} *`}
                    value={originConfig.getEditedIdentifier(editedDocument)}
                    onChange={(e) =>
                      setEditedDocument(
                        originConfig.setEditedIdentifier(
                          editedDocument,
                          e.target.value,
                        ),
                      )
                    }
                  />
                  <Typography
                    variant="caption"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    * Rut sin puntos y con guión
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Monto"
                    value={formatMoney(editedDocument.amount)}
                    onChange={(e) =>
                      setEditedDocument({
                        ...editedDocument,
                        amount: formatMoneyBack(e.target.value) || 0,
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            <Box>
              <SmallHeaderWithIcon
                icon={<FindInPage fontSize="small" />}
                title="Detalles Adicionales"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Fecha de emisión"
                    type="date"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={
                      editedDocument.issuedDate
                        ? formatDateBack(editedDocument.issuedDate)
                        : ''
                    }
                    onChange={handleChangeDate}
                  />
                </Grid>
                {isInvoice(document) && (
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id="subtype-select-label">Tipo</InputLabel>
                      <Select
                        labelId="subtype-select-label"
                        id="subtype-select"
                        label="Tipo"
                        value={editedDocument.subType}
                        onChange={(e) =>
                          setEditedDocument({
                            ...editedDocument,
                            subType: String(e.target.value),
                          })
                        }
                      >
                        <MenuItem value="electronic-invoice">
                          Factura electrónica
                        </MenuItem>
                        <MenuItem value="electronic-invoice-exempt">
                          Factura exenta
                        </MenuItem>
                        <MenuItem value="electronic-invoice-buyer">
                          Factura de compra
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Box>
        </DialogContent>
      )}
      <DialogActions>
        <CustomButton
          variant="contained"
          color="secondary"
          onClick={() => setOpen(false)}
        >
          Cancelar
        </CustomButton>
        <CustomButton
          variant="contained"
          color="primary"
          onClick={() => {
            void handleSave();
          }}
        >
          Guardar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditDocumentManually;
